/**
 * PlayAd "Tasks" panel: floating bottom-right list of the user's background work (reference analysis,
 * generation batches, onboarding, sweeps, reports) toggled by the layers button.
 *
 *   GET /api/tasks → { tasks: TaskCard[] } (automation `describeJob`): { id, kind, brandId, title, subtitle,
 *     state: "pending"|"running"|"done"|"failed"|"cancelled", status (raw job status), createdAt, finishedAt,
 *     resultRef, action: { label, url } | null }
 *   Fallback when the endpoint is missing (404): tool `jobs.list {limit: 15}` → { jobs: [{ id, kind, status, brandId, payload,
 *   result, lastError, createdAt, finishedAt }] } mapped to friendly titles below.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, CircleX, Layers, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { api, ApiError, callTool } from "@/api/client";
import { pickArray } from "@/api/types";
import { cn } from "@/lib/cn";
import { flowEditorPath } from "@/lib/flows";
import { safeInAppHref } from "@/lib/safe-url";
import { relative } from "@/lib/format";
import { brandPath } from "@/state/brand";

export interface TaskItem {
  id: string;
  kind?: string;
  title: string;
  subtitle?: string;
  /** Normalised: queued | running | succeeded | failed | cancelled */
  status: string;
  brandId?: string | null;
  createdAt?: string;
  finishedAt?: string | null;
  action?: { label: string; href: string } | null;
}

export interface JobRow {
  id: string;
  kind: string;
  status: string;
  brandId?: string | null;
  payload?: Record<string, unknown>;
  result?: Record<string, unknown> | null;
  lastError?: string | null;
  createdAt?: string;
  finishedAt?: string | null;
}

const JOB_TITLES: Record<string, { title: string; running: string; done: string }> = {
  "creative.generate_batch": { title: "Image Generation", running: "Generating your creatives", done: "Your creative is ready" },
  "creative.generate_from_idea": { title: "Idea → Creative", running: "Generating from the idea", done: "Your creative is ready" },
  "creative.bench_rebuild": { title: "Bench Rebuild", running: "Iterating on fatigued winners", done: "New variants are ready" },
  "intel.analyze_reference": { title: "Reference Analysis", running: "Analyzing the reference", done: "Reference analysis complete" },
  "intel.onboard_brand": { title: "Brand Onboarding", running: "Scraping site, reviews and competitors", done: "Brand profile drafted — review it" },
  "intel.competitor_sweep": { title: "Competitor Sweep", running: "Sweeping competitor ad libraries", done: "New port-mapped ideas are ready" },
  "intel.refresh_reviews": { title: "App Reviews", running: "Mining new reviews", done: "Hook bank updated" },
  "ads.sync_structure": { title: "Account Sync", running: "Syncing campaigns and ads", done: "Structure synced" },
  "ads.sync_metrics": { title: "Metrics Sync", running: "Pulling platform metrics", done: "Metrics synced" },
  "insights.daily_brief": { title: "Daily Brief", running: "Writing the daily brief", done: "Brief published" },
  "check.prepare": { title: "Daily Ads Check", running: "Scoring yesterday against the last 7 days", done: "Daily check ready" },
  "agent.run_prompt": { title: "Agent Run", running: "Mimic is working", done: "Reply posted in chat" },
  "learning.update": { title: "Learning Update", running: "Updating the playbook", done: "Playbook updated" },
  "flows.run": { title: "Flow Run", running: "Currently generating...", done: "Your creative is ready" },
};

/** PlayAd's Flow Run task copy ("Flow Run" · "Currently generating..." / "Your creative is ready" · "Go To Flow"). */
const FLOW_RUN = { title: "Flow Run", running: "Currently generating...", done: "Your creative is ready", go: "Go To Flow" } as const;
const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);

/** In-app editor path for a flow run (server url when it is a safe in-app path, else built from ids). */
function flowRunHref(brandId: string | null | undefined, ref: { url?: unknown; id?: unknown } | null | undefined, flowId?: unknown): string | null {
  const fromUrl = safeInAppHref(ref?.url);
  if (fromUrl) return fromUrl;
  const id = str(flowId) ?? str(ref?.id);
  return id && brandId ? flowEditorPath(brandId, id) : null;
}

export function fromJob(j: JobRow): TaskItem {
  const meta = JOB_TITLES[j.kind] ?? { title: j.kind.split(".").pop()!.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()), running: "Running", done: "Completed" };
  const ok = j.status === "succeeded";
  const failed = j.status === "failed" || j.status === "dead";
  let action: TaskItem["action"] = null;
  const batchId = (j.result?.batchId ?? j.payload?.batchId) as string | undefined;
  if (ok && j.brandId && (j.kind.startsWith("creative.") || batchId)) action = { label: "Go To Studio", href: `${brandPath(j.brandId, "studio")}${batchId ? `?batch=${batchId}` : ""}` };
  if (ok && j.kind === "intel.onboard_brand" && j.brandId) action = { label: "Review", href: `/brands/${j.brandId}/onboarding` };
  if (ok && j.kind === "intel.competitor_sweep" && j.brandId) action = { label: "See ideas", href: `${brandPath(j.brandId, "inspiration")}?tab=ideas` };
  const threadId = (j.result?.threadId ?? j.payload?.threadId) as string | undefined;
  if (ok && threadId) action = { label: "Open chat", href: `/chat/${threadId}` };
  if (j.kind === "flows.run") {
    const href = flowRunHref(j.brandId, j.result?.resultRef as { url?: unknown; id?: unknown } | undefined, j.result?.flowId ?? j.payload?.flowId);
    action = href ? { label: FLOW_RUN.go, href } : null;
  }
  return {
    id: j.id,
    kind: j.kind,
    title: meta.title,
    subtitle: failed ? (j.lastError ?? "Failed") : ok ? meta.done : meta.running,
    status: failed ? "failed" : j.status,
    brandId: j.brandId,
    createdAt: j.createdAt,
    finishedAt: j.finishedAt,
    action,
  };
}

export interface TaskCard {
  id: string;
  kind?: string;
  brandId?: string | null;
  title?: string;
  subtitle?: string;
  state?: string;
  status?: string;
  createdAt?: string;
  finishedAt?: string | null;
  action?: { label: string; url?: string; href?: string } | null;
  resultRef?: { type?: string; id?: string; url?: string } | null;
}

export function fromCard(t: TaskCard): TaskItem {
  if (t.kind === "flows.run") return fromFlowRunCard(t);
  const state = t.state ?? "";
  const status =
    state === "done" ? "succeeded" : state === "pending" ? "queued" : state === "running" || state === "failed" || state === "cancelled" ? state : (t.status === "dead" ? "failed" : (t.status ?? "queued"));
  // Server-supplied target: only same-origin, http(s)/root-relative paths become in-app links.
  const path = safeInAppHref(t.action?.url ?? t.action?.href);
  return {
    id: t.id,
    kind: t.kind,
    title: t.title ?? t.kind ?? "Task",
    subtitle: t.subtitle,
    status,
    brandId: t.brandId,
    createdAt: t.createdAt,
    finishedAt: t.finishedAt,
    action: t.action?.label && path ? { label: t.action.label, href: path } : null,
  };
}

/**
 * `flows.run` jobs: PlayAd copy even before/without server-side task copy (the generic server fallback is "Run" /
 * "Running" / "Completed"), and a "Go To Flow" button while running too. The target is an in-app path only.
 */
function fromFlowRunCard(t: TaskCard): TaskItem {
  const base = fromCard({ ...t, kind: undefined });
  const generic = (v: string | undefined, ...g: string[]) => !v || g.includes(v);
  const running = base.status === "running" || base.status === "queued";
  const subtitle = running
    ? generic(t.subtitle, "Running", "Queued") ? FLOW_RUN.running : t.subtitle
    : base.status === "succeeded" && generic(t.subtitle, "Completed", "Done") ? FLOW_RUN.done : t.subtitle;
  const href = base.action?.href ?? flowRunHref(t.brandId, t.resultRef);
  return {
    ...base,
    kind: t.kind,
    title: generic(t.title, "Run", "flows.run") ? FLOW_RUN.title : t.title!,
    subtitle,
    action: href ? { label: t.action?.label ?? FLOW_RUN.go, href } : null,
  };
}

async function fetchTasks(signal?: AbortSignal): Promise<TaskItem[]> {
  try {
    return pickArray<TaskCard>(await api("/api/tasks", { signal }), "tasks", "items").map(fromCard);
  } catch (e) {
    if (!(e instanceof ApiError) || (e.status !== 404 && e.status !== 405)) throw e;
    const res = await callTool("jobs.list", { limit: 15 }, { signal });
    return pickArray<JobRow>(res, "jobs").map(fromJob);
  }
}

const running = (t: TaskItem) => t.status === "running" || t.status === "queued";

export function TasksDock() {
  const [open, setOpen] = useState(false);
  // Chat pages have a bottom composer — lift the dock above it.
  const onChat = useLocation().pathname.startsWith("/chat");
  const q = useQuery({
    queryKey: ["tasks"],
    queryFn: ({ signal }) => fetchTasks(signal),
    refetchInterval: (query) => ((query.state.data ?? []).some(running) ? 4000 : open ? 15_000 : 60_000),
    retry: false,
    staleTime: 3000,
  });
  const tasks = useMemo(() => (q.data ?? []).slice(0, 12), [q.data]);
  const active = tasks.filter(running).length;

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="Tasks"
          className={cn(
            "fixed right-3 z-40 flex max-h-[60vh] w-[min(380px,calc(100vw-1.5rem))] animate-pop-in flex-col overflow-hidden rounded-2xl border border-line-strong bg-[#1b1b1d] shadow-pop md:right-[5.5rem]",
            onChat ? "bottom-56 md:bottom-36" : "bottom-36 md:bottom-6",
          )}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <span className="text-[15px] font-semibold">Tasks</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close tasks" className="grid size-7 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg">
              <X className="size-4" />
            </button>
          </div>
          <div className="flex flex-col gap-2 overflow-y-auto p-3 scrollbar-thin">
            {q.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-[12.5px] text-muted">
                <Loader2 className="size-4 animate-spin" /> Loading tasks…
              </div>
            ) : q.error ? (
              <div className="py-8 text-center text-[12.5px] text-muted">Tasks are unavailable right now.</div>
            ) : tasks.length === 0 ? (
              <div className="py-8 text-center text-[12.5px] text-muted">No background tasks. Generations, analyses and syncs show up here.</div>
            ) : (
              tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5">
                  {running(t) ? (
                    <Loader2 className="size-6 shrink-0 animate-spin text-lavender" />
                  ) : t.status === "failed" || t.status === "cancelled" ? (
                    <CircleX className="size-6 shrink-0 text-danger" strokeWidth={1.75} />
                  ) : (
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary text-white">
                      <Check className="size-3.5" strokeWidth={3} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold">{t.title}</div>
                    <div className={cn("truncate text-[12px]", t.status === "failed" ? "text-danger" : "text-muted")}>
                      {t.subtitle}
                      {t.finishedAt && !running(t) ? ` · ${relative(t.finishedAt)}` : ""}
                    </div>
                  </div>
                  {t.action && safeInAppHref(t.action.href) && (
                    <Link to={safeInAppHref(t.action.href)!} onClick={() => setOpen(false)} className="shrink-0 rounded-lg bg-primary px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-primary-hover">
                      {t.action.label}
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={active ? `Tasks (${active} running)` : "Tasks"}
          title="Tasks"
          aria-expanded={open}
          className={cn(
            "fixed right-3 z-40 grid size-12 place-items-center rounded-full border border-line-strong bg-[#1b1b1d] text-fg shadow-pop transition hover:bg-elevated md:right-6",
            onChat ? "bottom-40 md:bottom-36" : "bottom-20 md:bottom-6",
            open && "bg-elevated",
          )}
        >
          <Layers className="size-5" strokeWidth={1.75} />
          {active > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10.5px] font-bold text-white ring-2 ring-canvas">{active}</span>
          )}
        </button>
    </>
  );
}
